import TalkToTheBookSource from "@/interfaces/TalkToTheBookSource";

export interface TaazeAIService {
    upload(url: string, sessionId: string): Promise<boolean>; // Method to upload URL
    prompt(url: string, prompt: string, userId: string, sessionId: string, onStreamUpdate: (content: string | null, promptSuggestions: string | null, sources: TalkToTheBookSource[]) => void): Promise<void>; // Method to start the stream after upload
}

export class TalkToTheBookAIService implements TaazeAIService {
    private TIMEOUT = 60000;

    private baseUrl: string;

    private uploadEndpoint = '/v1/ai/talk_to_the_book/upsert';
    private promptEndpoint = '/v1/ai/talk_to_the_book/prompt';
    private predefinedQuestionsEndpoint = '/v1/ai/talk_to_the_book/predefined_questions';

    private hasUploaded = false;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    async upload(productId: string, sessionId: string): Promise<boolean> {
        const url = `${this.baseUrl}${this.uploadEndpoint}`;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.TIMEOUT);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ product_id: productId, session_id: sessionId }),
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error('Failed to upload URL');
            }

            this.hasUploaded = true;

            return this.hasUploaded;
        } catch (error) {
            console.error('Upload failed:', error);
            throw error;
        } finally {
            clearTimeout(timer);
        }
    }

    async prompt(productId: string, prompt: string, userId: string, sessionId: string, onStreamUpdate: (content: string | null, promptSuggestions: string | null, sources: TalkToTheBookSource[]) => void): Promise<void> {
        const url = `${this.baseUrl}${this.promptEndpoint}`;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.TIMEOUT);

        try {
            if (!this.hasUploaded) {
                await this.upload(productId, sessionId);
            }

            let promptSuggestions = '';
            let sources: TalkToTheBookSource[] = [];

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ prompt: prompt, product_id: productId, user_id: userId, session_id: sessionId }),
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error('Failed to start streaming');
            }

            clearTimeout(timer);

            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // eslint-disable-next-line
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    onStreamUpdate(null, promptSuggestions, sources)
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split('\n\n');
                buffer = lines.pop() || '';
                for (const line of lines) {
                    if (!line.trim()) {continue;}

                    try {
                        const parsedData = JSON.parse(line);
                        if (parsedData.content) {
                            onStreamUpdate(parsedData.content, null, []);
                        }
                        if (parsedData.prompt) {
                            promptSuggestions += parsedData.prompt;
                        }
                        if (parsedData.sources) {
                            sources = parsedData.sources;
                        }
                        console.log(parsedData)
                    } catch (error) {
                        console.error('Failed to parse data:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Streaming failed:', error);
            throw error;
        } finally {
            clearTimeout(timer);
        }
    }

    async getPredefinedQuestions(): Promise<{ [key: number]: string }> {
        const url = `${this.baseUrl}${this.predefinedQuestionsEndpoint}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to get predefined questions');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Failed to get predefined questions:', error);
            throw new Error('Failed to get predefined questions');
        }
    }
}
